/**
 * shop-nav-v2-wiring.js
 * ---------------------
 * Feeds search to fragno-global-nav-v2, which draws the field and renders whatever its
 * `results` attribute holds but has no submit, loading state or result keyboard of its own:
 * - Typeahead: store results from /stores.json written into `results`
 * - Frames: each result link carries its store's logo frame color as `--store-frame-color`,
 *   set again whenever fragno renders the cards
 * - Short, in flight or failed: the empty state reads as a hint (`data-short-query-heading`,
 *   `data-searching-heading`, `data-search-error-heading`) in place of "No matches yet"
 * - Arrow keys: move focus from the field through the search panel's links and back
 * - Enter: sends the query to the offers search page
 * - Compact: measures where the card ends so the panel can fill the rest of the viewport
 * - Account menu: the avatar pill's click toggles `shop-profile-dropdown`, which draws the menu
 * - Sign out: the drawer's button submits the dropdown's logout form
 * - Shop All: fragno lists the category's own link first on a compact category page (the
 *   `showAll` items, text from `all-label`); it moves into the page's heading row, flush right
 * - Current link: fragno drops `selected` from `supporting-links`, so the one named by
 *   `data-supporting-current` is marked selected here, again whenever fragno rebuilds the bar
 */

import { searchResultHref } from "./search_result_href.js";
import { suggestionText } from "./search_suggestion_text.js";

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 8;
const SEARCH_INPUT = '[data-ref="search-input"]';
const SEARCH_LINKS = ".fragno-global-nav-v2__panel--search a[href]";
const RESULT_CARD = ".fragno-global-nav-v2__result-card";
const FRAME_COLOR = /^#[0-9a-f]{6}$/i;
const SUPPORTING_LINKS = ".fragno-global-nav-v2__supporting a";
const DRAWER_HEADING = ".fragno-global-nav-v2__drawer-heading";
const DRAWER_ALL_ITEM =
  ".fragno-global-nav-v2__drawer-deep-links > li:first-child";
const CATEGORY_BUTTON = ".fragno-global-nav-v2__local-list button";

let _initialized = false;

function initNavV2Wiring({
  navigate = (url) => {
    window.location.href = url;
  },
} = {}) {
  if (_initialized) return;

  const nav = document.querySelector("fragno-global-nav-v2");
  if (!nav) return;

  _initialized = true;

  let debounce;
  let abort;
  let lastItems = [];

  const input = () => nav.querySelector(SEARCH_INPUT);
  const links = () => [...nav.querySelectorAll(SEARCH_LINKS)];
  const dropdown = () => document.querySelector("shop-profile-dropdown");

  const emptyHeading = nav.getAttribute("no-results-heading");
  const hints = {
    searching: nav.dataset.searchingHeading,
    short: nav.dataset.shortQueryHeading,
    error: nav.dataset.searchErrorHeading,
  };

  /** The no-results block doubles as the hint while a query is short, in flight or failed. */
  const emptyState = (state) => {
    const hint = hints[state];
    nav.toggleAttribute("hide-results-illustration", Boolean(hint));
    if (hint) nav.setAttribute("data-search-state", state);
    else nav.removeAttribute("data-search-state");
    if (hint || emptyHeading)
      nav.setAttribute("no-results-heading", hint || emptyHeading);
    else nav.removeAttribute("no-results-heading");
  };

  /** fragno takes no per-result style, so each store's frame color rides on its result link. */
  const paintFrames = () =>
    links().forEach((link, i) => {
      const color = lastItems[i]?.frameColor;
      if (FRAME_COLOR.test(color))
        link.style.setProperty("--store-frame-color", color);
      else link.style.removeProperty("--store-frame-color");
    });

  /**
   * fragno rebuilds the panel on every attribute write, so a focused card is re-found by
   * its store path, else by position when that store left the results.
   */
  const showResults = (items, state = "empty") => {
    const focused = document.activeElement;
    const index = links().indexOf(focused);
    emptyState(state);
    lastItems = items;
    nav.setAttribute("results", JSON.stringify(items));
    paintFrames();
    if (index === -1) return;
    const fresh = links();
    const same = fresh.find((link) => link.pathname === focused.pathname);
    (same || fresh[Math.min(index, fresh.length - 1)] || input())?.focus();
  };

  const fail = (reason) => {
    console.error("[shop-nav-v2-wiring] store search failed:", reason);
    showResults([], "error");
  };

  function search(query) {
    clearTimeout(debounce);
    abort?.abort();
    if (query.length === 0) return showResults([]);
    if (query.length < MIN_QUERY_LENGTH) return showResults([], "short");

    emptyState("searching");
    debounce = setTimeout(async () => {
      const controller = new AbortController();
      abort = controller;
      try {
        const resp = await fetch(
          `/stores.json?q=${encodeURIComponent(query)}`,
          {
            headers: { Accept: "application/json" },
            credentials: "include",
            signal: controller.signal,
          },
        );
        if (!resp.ok) return fail(resp.status);
        const data = await resp.json();
        if (controller.signal.aborted) return;
        showResults(
          (data.results || []).slice(0, MAX_RESULTS).map((store) => ({
            title: store.name,
            description: suggestionText(store).subtitle,
            image: store.logo_attachment,
            frameColor: store.logo_frame_color,
            href: searchResultHref(
              store.url,
              data.search_id,
              data.query_version,
            ),
          })),
        );
      } catch (error) {
        if (!controller.signal.aborted) fail(error);
      }
    }, DEBOUNCE_MS);
  }

  /** Columns of the grid holding `link`, so ArrowDown moves a row, not a card. */
  const columnsAround = (link) => {
    const list = link.closest("ul");
    if (!list) return 1;
    const tracks = getComputedStyle(list).gridTemplateColumns.split(" ");
    return tracks.filter(Boolean).length || 1;
  };

  function moveFocus(event) {
    const { key, target, altKey, ctrlKey, metaKey, shiftKey, isComposing } =
      event;
    if (altKey || ctrlKey || metaKey || shiftKey || isComposing) return;
    const targets = links();
    if (targets.length === 0) return;

    if (target.matches?.(SEARCH_INPUT)) {
      if (key !== "ArrowDown") return;
      event.preventDefault();
      return targets[0].focus();
    }

    const index = targets.indexOf(target);
    if (index === -1) return;
    const forward = key === "ArrowDown" || key === "ArrowRight";
    const vertical = key === "ArrowDown" || key === "ArrowUp";
    const step = (forward ? 1 : -1) * (vertical ? columnsAround(target) : 1);
    event.preventDefault();
    const next = index + step;
    if (next < 0) return input()?.focus();
    targets[Math.min(next, targets.length - 1)].focus();
  }

  nav.addEventListener("fragno-global-nav-v2-search-input", (event) =>
    search((event.detail?.value || "").trim()),
  );
  nav.addEventListener("fragno-global-nav-v2-search-clear", () => search(""));

  /** The category's own link goes to the heading row, flush right, as a "Shop all" text link. */
  const moveShopAllToHeading = () => {
    const label = nav.getAttribute("all-label");
    const heading = nav.querySelector(DRAWER_HEADING);
    const item = nav.querySelector(DRAWER_ALL_ITEM);
    const link = item?.querySelector("a");
    if (!label || !heading || link?.textContent !== label) return;

    const shopAll = document.createElement("a");
    shopAll.className = "shop-nav-v2__shop-all";
    shopAll.setAttribute("href", link.getAttribute("href"));
    shopAll.setAttribute(
      "aria-label",
      `${label} ${heading.textContent.trim()}`,
    );
    shopAll.textContent = label;
    heading.append(shopAll);
    item.remove();
  };

  const markCurrentSupportingLink = () => {
    const current = nav.dataset.supportingCurrent;
    nav.querySelectorAll(SUPPORTING_LINKS).forEach((link) => {
      if (!current || link.getAttribute("href") !== current) return;
      link.classList.add("fragno-global-nav-v2__bar-link--active");
      link.setAttribute("aria-current", "page");
    });
  };
  markCurrentSupportingLink();
  moveShopAllToHeading();

  // fragno also renders the cards and the bar on its own (compact toggle, other attribute writes).
  new MutationObserver((mutations) => {
    markCurrentSupportingLink();
    moveShopAllToHeading();
    const rendered = mutations.some(({ addedNodes }) =>
      [...addedNodes].some(
        (node) =>
          node.matches?.(RESULT_CARD) || node.querySelector?.(RESULT_CARD),
      ),
    );
    if (rendered) paintFrames();
  }).observe(nav, { childList: true, subtree: true });

  // The compact search panel fills the viewport below the card (_global-nav-v2-next.scss);
  // the card's bottom edge moves with the merchandising ribbon and the scrolled pill.
  const measureSearchTop = () => {
    if (!nav.hasAttribute("search-open")) return;
    const shell = nav.querySelector(".fragno-global-nav-v2__shell");
    if (!shell) return;
    const top = shell.getBoundingClientRect().bottom;
    nav.style.setProperty("--shop-nav-v2-search-top", `${top}px`);
  };
  nav.addEventListener("fragno-global-nav-v2-search-toggle", measureSearchTop);
  window.addEventListener("resize", measureSearchTop);

  nav.addEventListener("fragno-global-nav-v2-account-click", () =>
    dropdown()?.toggleAttribute("open"),
  );
  nav.addEventListener("fragno-global-nav-v2-sign-out-click", () =>
    dropdown()
      ?.querySelector("form.shop-profile-dropdown__logout-form")
      ?.requestSubmit(),
  );

  // fragno 2.4.0 moves focus along the category row with `preventScroll`
  // (global-nav-v2.js _onTriggerKeydown), and Shop's row scrolls on desktop
  // (upstream/_global-nav-v2-next.scss), so a category past the card edge would
  // take focus out of view. Delete when fragno scrolls the row itself.
  nav.addEventListener("focusin", (event) => {
    if (!event.target.matches?.(CATEGORY_BUTTON)) return;
    event.target.scrollIntoView({ block: "nearest", inline: "nearest" });
  });

  nav.addEventListener("keydown", (event) => {
    if (event.key?.startsWith("Arrow")) return moveFocus(event);
    if (event.key !== "Enter") return;
    if (!event.target.matches?.(SEARCH_INPUT)) return;
    const query = event.target.value.trim();
    if (!query) return;
    event.preventDefault();
    navigate(`/offers?q=${encodeURIComponent(query)}`);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initNavV2Wiring);
} else {
  initNavV2Wiring();
}

export { initNavV2Wiring };
